import { FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { InputWidget, SelectDropdownWidget } from 'src/components/widget';
import {
  SCREEN_CONFIGURATION_LABELS,
  ScreenLayoutTypeEnum,
} from 'src/constants';
import { useGetAllLayoutType, useGetProjectConfig } from 'src/hooks';
import { LayoutTypes } from 'src/interfaces';
import { getScreenConfigurationLabel, lang } from 'src/utils';

import { GeneralSettingsProps } from './GeneralSettings.interface';
import {
  LayoutContainer,
  LayoutTypeContainer,
  ScreenBox,
  ScreensContainer,
} from './GeneralSettings.styled';

export const GeneralSettings = ({
  control,
  setValue,
  handleChannelChange,
  channelInfo,
  selectedChannelInfo,
  watch,
}: GeneralSettingsProps) => {
  const { data: layoutTypes = [] } = useGetAllLayoutType();
  const {
    data: { globalComponentsScreens = {} } = {},
    isLoading: isProjectConfigLoading,
  } = useGetProjectConfig();

  const handleChange = (value: any, field: any) => {
    const trimmedString = value.trim() === '' ? '' : value.replace(/^\s+/, '');
    setValue('name', trimmedString);
    field.onChange(trimmedString);
  };
  const channelOptions = channelInfo?.map((channel) => ({
    label: channel.channel_name ?? '',
    value: channel.channel_name ?? '',
  }));

  const singleLayoutTypeName =
    selectedChannelInfo &&
    layoutTypes.find(
      (type) => type.id === selectedChannelInfo.layout_type_id[0],
    )?.name;

  const screenDimensions = selectedChannelInfo?.screens ?? [
    { width: 0, height: 0 },
  ];
  const screenConfigLabel = getScreenConfigurationLabel(screenDimensions);
  const screenCount = selectedChannelInfo?.screens?.length ?? 0;

  const isTwoVerticalScreens =
    screenConfigLabel === SCREEN_CONFIGURATION_LABELS.TWO_VERTICAL_SCREENS;

  const screenLayoutTypeOptions = useMemo(() => {
    if (!isTwoVerticalScreens || screenCount !== 2 || isProjectConfigLoading) {
      return [];
    }

    const screensConfig = globalComponentsScreens[
      SCREEN_CONFIGURATION_LABELS.TWO_VERTICAL_SCREENS
    ] as
      | Array<{
          screenLayoutType?: ScreenLayoutTypeEnum;
        }>
      | undefined;

    if (!Array.isArray(screensConfig) || screensConfig.length === 0) {
      return [];
    }

    const uniqueTypes = Array.from(
      new Set(
        screensConfig
          .map((config) => config?.screenLayoutType)
          .filter((type): type is ScreenLayoutTypeEnum => !!type),
      ),
    );

    return uniqueTypes.map((type) => ({
      label: type,
      value: type,
    }));
  }, [
    isTwoVerticalScreens,
    globalComponentsScreens,
    screenCount,
    isProjectConfigLoading,
  ]);

  const selectedLayoutTypeId = watch ? watch('layout_type_id') : undefined;
  const isSelectedLayoutTypeOutdoor = useMemo(() => {
    const selectedLayoutType = layoutTypes.find(
      (type) => type.id === Number(selectedLayoutTypeId),
    );

    return selectedLayoutType?.name === LayoutTypes.OUTDOOR;
  }, [layoutTypes, selectedLayoutTypeId]);

  return (
    <>
      <Typography variant="h6" marginBottom={2}>
        {lang.assemblyLayouts.generalSettings.title}
      </Typography>
      <Controller
        name="name"
        control={control}
        defaultValue=""
        rules={{
          required: lang.assemblyLayouts.generalSettings.fieldRequired,
          maxLength: {
            value: 100,
            message: lang.assemblyLayouts.generalSettings.layoutNameMaximum,
          },
        }}
        render={({ field, fieldState }) => (
          <InputWidget
            {...field}
            label={lang.assemblyLayouts.generalSettings.layoutName}
            onChange={(value) => handleChange(value, field)}
            value={field?.value}
            placeholder={
              lang.assemblyLayouts.generalSettings.layoutNamePlaceholder
            }
            hasError={!!fieldState?.error}
            error={fieldState?.error?.message}
          />
        )}
      />
      <Controller
        name="channel_name"
        control={control}
        defaultValue=""
        rules={{ required: lang.assemblyLayouts.generalSettings.fieldRequired }}
        render={({ field, fieldState }) => (
          <SelectDropdownWidget
            {...field}
            label={lang.assemblyLayouts.generalSettings.channelLabel}
            value={field?.value}
            onChange={(e) => {
              field.onChange(e);
              handleChannelChange(e);
            }}
            placeholder={
              lang.assemblyLayouts.generalSettings.channelPlaceholder
            }
            selectOptions={channelOptions}
            hasError={!!fieldState?.error}
            error={fieldState?.error?.message}
          />
        )}
      />

      {isTwoVerticalScreens &&
        screenLayoutTypeOptions.length > 0 &&
        isSelectedLayoutTypeOutdoor && (
          <Controller
            name="screen_layout_type"
            control={control}
            defaultValue=""
            rules={{
              required: lang.assemblyLayouts.generalSettings.fieldRequired,
            }}
            render={({ field, fieldState }) => (
              <SelectDropdownWidget
                {...field}
                label="Screen Layout Type"
                value={field?.value}
                onChange={(e) => {
                  field.onChange(e);
                }}
                placeholder="Select a type"
                selectOptions={screenLayoutTypeOptions}
                hasError={!!fieldState?.error}
                error={fieldState?.error?.message}
              />
            )}
          />
        )}

      {selectedChannelInfo && (
        <LayoutContainer>
          <LayoutTypeContainer>
            <Typography variant="subtitle1" marginTop="16px">
              {lang.assemblyLayouts.generalSettings.channelTypeLabel}
            </Typography>
            {selectedChannelInfo.layout_type_id.length > 1 ? (
              <Controller
                name="layout_type_id"
                control={control}
                defaultValue=""
                rules={{
                  required: lang.assemblyLayouts.generalSettings.fieldRequired,
                }}
                render={({ field }) => (
                  <RadioGroup {...field} onChange={field?.onChange}>
                    {selectedChannelInfo.layout_type_id.map((layoutTypeId) => (
                      <FormControlLabel
                        key={layoutTypeId}
                        value={layoutTypeId}
                        control={<Radio />}
                        label={
                          layoutTypes.find((type) => type.id === layoutTypeId)
                            ?.name
                        }
                      />
                    ))}
                  </RadioGroup>
                )}
              />
            ) : (
              <Typography>{singleLayoutTypeName}</Typography>
            )}
          </LayoutTypeContainer>

          <div>
            <Typography variant="subtitle1" marginTop="16px">
              {lang.assemblyLayouts.generalSettings.screenConfigTitle}
            </Typography>
            <ScreensContainer>
              {screenDimensions.map((screen, index) => (
                <ScreenBox
                  key={index}
                  width={screen.width / 25}
                  height={screen.height / 25}
                />
              ))}
            </ScreensContainer>
            <Typography variant="body1">{screenConfigLabel}</Typography>
          </div>
        </LayoutContainer>
      )}
    </>
  );
};
